import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { db, getDocument, getDocuments, updateDocument } from '../services/firebase'
import { doc, onSnapshot, collection, orderBy, query, serverTimestamp, addDoc, getDocs } from 'firebase/firestore'
import { motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  TrophyIcon,
  CurrencyEuroIcon,
  ClockIcon,
  ShareIcon,
  UserPlusIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as WaitingIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
  TrashIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it, enUS } from 'date-fns/locale'
import { FaLink, FaWhatsapp, FaFacebookF, FaInstagram } from 'react-icons/fa'
import { calculateDistance, getDerivedMatchStatus, getMatchRosterCapacity, pruneInvitedIfRosterFull } from '../utils/helpers'
import { resolveNotificationMessage, matchTitleI18nParams } from '../utils/notificationMessage'
import { createNotification } from '../services/notifications'

function nameInitialLetter(name) {
  const trimmed = String(name ?? '').trim()
  if (!trimmed) return '?'
  const first = [...trimmed][0]
  return first ? first.toUpperCase() : '?'
}

function normalizeUserRoleKey(role) {
  return String(role || '').trim().toLowerCase().replace(/\s+/g, '_')
}

/** Matches admin / invite exclusions so center staff can use venue QR check-in. */
function isCenterManagerRole(role) {
  const norm = normalizeUserRoleKey(role)
  return (
    norm === 'center_manager'
    || norm === 'centermanager'
    || norm === 'center-manager'
    || norm === 'centerr_manager'
    || norm === 'center_manager_'
  )
}

function ParticipantAvatar({ profileSrc, name, className, title }) {
  const [imageFailed, setImageFailed] = useState(false)

  useEffect(() => {
    setImageFailed(false)
  }, [profileSrc])

  const showImage = Boolean(profileSrc) && !imageFailed
  const initial = nameInitialLetter(name)

  return (
    <div className={className} title={title}>
      {showImage ? (
        <img
          src={profileSrc}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="select-none">{initial}</span>
      )}
    </div>
  )
}

const MatchDetails = () => {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, userData } = useAuth()
  const currentUserData = userData
  const { t, i18n } = useTranslation()
  const locale = i18n.language?.startsWith('it') ? it : enUS
  const navigate = useNavigate()
  const [match, setMatch] = useState(null)
  const [loading, setLoading] = useState(true)
  const [participantStatus, setParticipantStatus] = useState(null)
  const [showQR, setShowQR] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [message, setMessage] = useState('')
  const [chatMessages, setChatMessages] = useState([])
  const [replyTo, setReplyTo] = useState(null)
  const [mentionQuery, setMentionQuery] = useState(null)
  const [showMentions, setShowMentions] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [playerSearch, setPlayerSearch] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteSearch, setInviteSearch] = useState('')
  const [invitePlayers, setInvitePlayers] = useState([])
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteSendingId, setInviteSendingId] = useState(null)
  const [inviteNearMeActive, setInviteNearMeActive] = useState(true)
  const [invitedPlayerIds, setInvitedPlayerIds] = useState([])
  const [showShareModal, setShowShareModal] = useState(false)
  const chatMessagesScrollRef = useRef(null)
  const inputRef = useRef(null)
  const hasProcessedCheckInRef = useRef(false)
  const cleanupInFlightRef = useRef(false)
  const lastCleanupSignatureRef = useRef(null)

  useEffect(() => {
    if (!match || !showInviteModal) return
    const cap = getMatchRosterCapacity(match)
    const acc = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (cap > 0 && acc >= cap) {
      setShowInviteModal(false)
    }
  }, [match, showInviteModal])

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'matches', id), (doc) => {
      if (doc.exists()) {
        const matchData = { id: doc.id, ...doc.data() }
        
        // Clean up order numbers on load
        if (matchData.participants && matchData.participants.length > 0) {
          const ordered = ensureValidOrderNumbers(
            matchData.participants,
            matchData.organizerId,
            matchData.organizerImage
          )
          const cleanedParticipants = pruneInvitedIfRosterFull(ordered, matchData)

          // Check if cleanup is needed
          const needsCleanup = JSON.stringify(cleanedParticipants) !== JSON.stringify(matchData.participants)
          
          if (needsCleanup) {
            const cleanupSignature = JSON.stringify(cleanedParticipants)
            const isOrganizerNow = matchData.organizerId === user?.uid

            // Only organizer writes cleanup changes, and only once per unique mismatch state.
            if (
              isOrganizerNow
              && !cleanupInFlightRef.current
              && lastCleanupSignatureRef.current !== cleanupSignature
            ) {
              cleanupInFlightRef.current = true
              lastCleanupSignatureRef.current = cleanupSignature

              updateDocument('matches', id, {
                participants: cleanedParticipants,
                updatedAt: new Date().toISOString()
              })
                .catch(err => {
                  // Allow retry on next snapshot if write fails.
                  lastCleanupSignatureRef.current = null
                  console.error('Error cleaning up order numbers:', err)
                })
                .finally(() => {
                  cleanupInFlightRef.current = false
                })
            }
          }
        }
        
        setMatch(matchData)
        
        const participant = matchData.participants?.find(p => p.userId === user?.uid)
        if (participant) {
          setParticipantStatus(participant.status)
        } else {
          setParticipantStatus(null)
        }
      } else {
        toast.error(t('matchDetails.matchNotFound'))
        navigate('/matches')
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [id, user?.uid, navigate, t])

  useEffect(() => {
    const chatRef = collection(db, 'matches', id, 'chats')
    const q = query(chatRef, orderBy('createdAt', 'asc'))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setChatMessages(messages)

      if (user && match) {
        const lastRead = match.chatLastRead?.[user.uid]
        if (lastRead) {
          const lastReadDate = lastRead.toDate ? lastRead.toDate() : new Date(lastRead)
          const unread = messages.filter(m =>
            m.senderId !== user.uid &&
            m.createdAt?.toDate &&
            m.createdAt.toDate() > lastReadDate
          ).length
          setUnreadCount(unread)
        } else if (messages.length > 0) {
          const unread = messages.filter(m => m.senderId !== user.uid).length
          setUnreadCount(unread)
        }
      }
    })

    return () => unsubscribe()
  }, [id, user?.uid, match?.chatLastRead])

  useEffect(() => {
    if (!showChat) return
    const scrollEl = chatMessagesScrollRef.current
    if (!scrollEl) return
    // scrollIntoView on the message list anchor scrolls the whole page on mobile; keep scroll inside the panel only
    const id = requestAnimationFrame(() => {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' })
    })
    return () => cancelAnimationFrame(id)
  }, [chatMessages, showChat])

  useEffect(() => {
    if (!showShareModal) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowShareModal(false)
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [showShareModal])

  useEffect(() => {
    if (searchParams.get('chat') === 'true') {
      const isOrganizer = match?.organizerId === user?.uid
      const isAccepted = match?.participants?.some(p => p.userId === user?.uid && p.status === 'accepted')
      const isCancelled = match?.status === 'cancelled'
      const canChat = !isCancelled && (isOrganizer || isAccepted)
      
      if (canChat) {
        setShowChat(true)
        searchParams.delete('chat')
        setSearchParams(searchParams)
      }
    }
  }, [searchParams, match, user])

  useEffect(() => {
    const checkInParam = searchParams.get('checkin')
    if (checkInParam !== 'true') {
      hasProcessedCheckInRef.current = false
      return
    }

    if (!user || !match || hasProcessedCheckInRef.current) return
    hasProcessedCheckInRef.current = true

    const performCheckIn = async () => {
      const clearCheckInParam = () => {
        const updatedParams = new URLSearchParams(searchParams)
        updatedParams.delete('checkin')
        setSearchParams(updatedParams)
      }

      const participant = (match.participants || []).find((p) => p.userId === user.uid)
      const isOrganizerUser = match.organizerId === user.uid
      const isAcceptedParticipant = participant?.status === 'accepted'
      const isCenterManager = isCenterManagerRole(userData?.role)

      const canUseParticipantPath =
        Boolean(participant)
        && (isOrganizerUser || isAcceptedParticipant || isCenterManager)
      const canUseStaffVenuePath =
        !participant && (isOrganizerUser || isCenterManager)

      if (!canUseParticipantPath && !canUseStaffVenuePath) {
        toast.error(t('matchDetails.checkInAcceptedOnly'))
        clearCheckInParam()
        return
      }

      const staffVenueCheckIns = match.staffVenueCheckIns || {}

      if (canUseParticipantPath) {
        const alreadyCheckedIn = Boolean(participant.checkedInAt)

        if (alreadyCheckedIn) {
          toast.success(t('matchDetails.checkInAlreadyDone'))
        } else {
          const updatedParticipants = (match.participants || []).map((p) =>
            p.userId === user.uid
              ? {
                  ...p,
                  checkedInAt: new Date().toISOString(),
                  attendanceStatus: 'present'
                }
              : p
          )

          await updateDocument('matches', id, {
            participants: updatedParticipants,
            updatedAt: new Date().toISOString()
          })

          toast.success(t('matchDetails.checkInSuccess'))

          // Notify organizer when participant checks in
          if (match.organizerId && match.organizerId !== user.uid) {
            const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
            const titleParams = matchTitleI18nParams(match)
            
            await sendMatchNotification({
              userId: match.organizerId,
              type: 'player_checked_in',
              messageI18nKey: 'matchDetails.playerCheckedInNotification',
              messageI18nParams: { ...titleParams, senderName },
              extra: {
                senderId: user.uid,
                senderName
              }
            })
          }
        }
      } else {
        const alreadyCheckedIn = Boolean(staffVenueCheckIns[user.uid]?.checkedInAt)

        if (alreadyCheckedIn) {
          toast.success(t('matchDetails.checkInAlreadyDone'))
        } else {
          await updateDocument('matches', id, {
            staffVenueCheckIns: {
              ...staffVenueCheckIns,
              [user.uid]: {
                checkedInAt: new Date().toISOString(),
                attendanceStatus: 'present',
                kind: isOrganizerUser ? 'organizer' : 'center_manager'
              }
            },
            updatedAt: new Date().toISOString()
          })

          toast.success(t('matchDetails.checkInSuccess'))
        }
      }

      clearCheckInParam()
    }

    performCheckIn().catch((error) => {
      console.error('Error processing check-in:', error)
      toast.error(t('matchDetails.checkInError'))
      const updatedParams = new URLSearchParams(searchParams)
      updatedParams.delete('checkin')
      setSearchParams(updatedParams)
    })
  }, [searchParams, user, userData?.role, match, id, t, setSearchParams])

  const markChatAsRead = async () => {
    if (!user || !match) return
    try {
      await updateDocument('matches', id, {
        [`chatLastRead.${user.uid}`]: serverTimestamp()
      })
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking chat as read:', error)
    }
  }

  const handleToggleChat = () => {
    const opening = !showChat
    setShowChat(opening)
    if (opening && canChat) {
      markChatAsRead()
    }
  }

  const handleSendMessage = async () => {
    if (!message.trim()) return
    if (!user) {
      toast.error(t('matchDetails.mustBeLoggedInToSend'))
      return
    }

    const mentionedNames = []
    const mentionRegex = /@(\w+)/g
    let m
    while ((m = mentionRegex.exec(message)) !== null) {
      mentionedNames.push(m[1])
    }

    try {
      const msgData = {
        sender: `${userData.firstName} ${userData.lastName}`,
        senderId: user.uid,
        message: message.trim(),
        createdAt: serverTimestamp()
      }

      if (replyTo) {
        msgData.replyTo = {
          id: replyTo.id,
          sender: replyTo.sender,
          message: replyTo.message
        }
      }

      if (mentionedNames.length > 0) {
        msgData.mentions = mentionedNames
      }

      await addDoc(collection(db, 'matches', id, 'chats'), msgData)

      const acceptedParticipants = match.participants?.filter(
        p => p.status === 'accepted' && p.userId !== user.uid
      ) || []

      const titleParams = matchTitleI18nParams(match)
      const previewSnippet = `${message.trim().substring(0, 50)}${message.trim().length > 50 ? '...' : ''}`
      const chatMsgKey = 'matchDetails.messagePreview'
      const chatMsgParams = { ...titleParams, message: previewSnippet }
      const notifPromises = acceptedParticipants.map(participant =>
        addDoc(collection(db, 'notifications'), {
          userId: participant.userId,
          type: 'chat_message',
          messageI18nKey: chatMsgKey,
          messageI18nParams: chatMsgParams,
          message: resolveNotificationMessage(t, {
            messageI18nKey: chatMsgKey,
            messageI18nParams: chatMsgParams,
            message: ''
          }),
          matchId: id,
          senderId: user.uid,
          senderName: `${userData.firstName} ${userData.lastName}`,
          read: false,
          createdAt: new Date().toISOString()
        })
      )

      await Promise.all(notifPromises)

      setMessage('')
      setReplyTo(null)
      setShowMentions(false)
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error(t('matchDetails.errorSendingMessage'))
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setMessage(value)

    const cursorPos = e.target.selectionStart
    const textBeforeCursor = value.substring(0, cursorPos)
    const atIndex = textBeforeCursor.lastIndexOf('@')

    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1)
      if (!textAfterAt.includes(' ') || textAfterAt.split(' ').length <= 1) {
        setMentionQuery(textAfterAt.toLowerCase())
        setShowMentions(true)
        return
      }
    }
    setShowMentions(false)
    setMentionQuery(null)
  }

  const insertMention = (participantName) => {
    const cursorPos = inputRef.current?.selectionStart || message.length
    const textBeforeCursor = message.substring(0, cursorPos)
    const atIndex = textBeforeCursor.lastIndexOf('@')
    const textAfterCursor = message.substring(cursorPos)

    const firstName = participantName.split(' ')[0]
    const newMessage = message.substring(0, atIndex) + `@${firstName} ` + textAfterCursor
    setMessage(newMessage)
    setShowMentions(false)
    setMentionQuery(null)
    inputRef.current?.focus()
  }

  const filteredParticipants = mentionQuery !== null
    ? (match?.participants || [])
        .filter(p => p.status === 'accepted' && p.userId !== user?.uid)
        .filter(p => p.name?.toLowerCase().includes(mentionQuery))
    : []

  const handleJoinMatch = async () => {
    if (!match) return
    if (!user || !userData) {
      toast.error(t('matchDetails.mustBeLoggedInToJoin'))
      return
    }

    const alreadyJoined = match.participants?.some(p => p.userId === user.uid)
    if (alreadyJoined) {
      toast.error(t('matchDetails.alreadyInMatch'))
      return
    }

    const mainCapacity = getMatchRosterCapacity(match)
    const acceptedOnRoster = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (mainCapacity > 0 && acceptedOnRoster >= mainCapacity) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    try {
      const isOrganizerJoining = match.organizerId === user.uid

      const newParticipant = {
        userId: user.uid,
        name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        role: userData.role || '',
        profileImage: userData.profileImage || '',
        status: isOrganizerJoining ? 'accepted' : 'pending',
        joinedAt: new Date().toISOString()
      }

      const updatedParticipants = [...(match.participants || []), newParticipant]

      let validatedParticipants = ensureValidOrderNumbers(updatedParticipants)
      validatedParticipants = pruneInvitedIfRosterFull(validatedParticipants, match)

      const updateData = {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      }

      if (isOrganizerJoining) {
        updateData.currentPlayers = (match.currentPlayers || 0) + 1
      }

      await updateDocument('matches', id, updateData)

      // Notify organizer when user requests to join
      if (!isOrganizerJoining && match.organizerId) {
        const senderName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)
        
        await sendMatchNotification({
          userId: match.organizerId,
          type: 'join_request',
          messageI18nKey: 'matchDetails.joinRequestNotification',
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })
      }

      toast.success(isOrganizerJoining ? t('matchDetails.joinedMatch') : t('matchDetails.requestSent'))
    } catch (error) {
      console.error('Error joining match:', error)
      toast.error(`Error: ${error.code || error.message || 'Unknown error'}`)
    }
  }

  const handleLeaveMatch = async () => {
    if (!match) return

    try {
      const leavingParticipant = match.participants.find(p => p.userId === user.uid)
      const wasAccepted = leavingParticipant?.status === 'accepted'
      const updatedParticipants = match.participants.filter(p => p.userId !== user.uid)
      
      // Re-assign order numbers after removal
      const validatedParticipants = ensureValidOrderNumbers(updatedParticipants)

      const updateData = {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      }

      if (wasAccepted) {
        updateData.currentPlayers = (match.currentPlayers || 1) - 1
      }

      await updateDocument('matches', id, updateData)

      toast.success(wasAccepted ? t('matchDetails.leftMatch') : t('matchDetails.requestCancelled'))
      setParticipantStatus(null)

      // Notify organizer and other participants when player leaves
      if (wasAccepted) {
        const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)
        const otherParticipants = match.participants.filter(p => p.userId !== user.uid && p.status === 'accepted' && p.userId !== match.organizerId)
        
        // Notify organizer
        if (match.organizerId && match.organizerId !== user.uid) {
          await sendMatchNotification({
            userId: match.organizerId,
            type: 'player_left',
            messageI18nKey: 'matchDetails.playerLeftNotification',
            messageI18nParams: { ...titleParams, senderName },
            extra: {
              senderId: user.uid,
              senderName
            }
          })
        }
        
        // Notify other accepted participants
        const notifyPromises = otherParticipants.map(participant =>
          sendMatchNotification({
            userId: participant.userId,
            type: 'player_left',
            messageI18nKey: 'matchDetails.playerLeftNotification',
            messageI18nParams: { ...titleParams, senderName },
            extra: {
              senderId: user.uid,
              senderName
            }
          })
        )
        await Promise.all(notifyPromises)
      } else if (leavingParticipant?.status === 'pending' && match.organizerId && match.organizerId !== user.uid) {
        // Notify organizer when pending request is cancelled
        const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)
        
        await sendMatchNotification({
          userId: match.organizerId,
          type: 'request_cancelled',
          messageI18nKey: 'matchDetails.requestCancelledNotification',
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })
      }
    } catch (error) {
      console.error('Error leaving match:', error)
      toast.error(t('matchDetails.errorLeavingMatch'))
    }
  }

  const handleAcceptInvite = async () => {
    if (!match || !user) return

    const mainCapacity = getMatchRosterCapacity(match)
    const acceptedOnRoster = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (mainCapacity > 0 && acceptedOnRoster >= mainCapacity) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    try {
      const updatedParticipants = match.participants.map(p => {
        if (p.userId === user.uid) {
          return { ...p, status: 'accepted' }
        }
        return p
      })

      let validatedParticipants = ensureValidOrderNumbers(updatedParticipants)
      validatedParticipants = pruneInvitedIfRosterFull(validatedParticipants, match)

      await updateDocument('matches', id, {
        participants: validatedParticipants,
        currentPlayers: (match.currentPlayers || 0) + 1,
        updatedAt: new Date().toISOString()
      })

      setParticipantStatus('accepted')
      toast.success(t('matchDetails.inviteAccepted'))

      const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
      const titleParams = matchTitleI18nParams(match)

      await sendMatchNotification({
        userId: match.organizerId,
        type: 'invite_accepted',
        messageI18nKey: 'matchDetails.inviteAcceptedNotification',
        messageI18nParams: { ...titleParams, senderName },
        extra: {
          senderId: user.uid,
          senderName
        }
      })

      // Also notify other accepted participants about the new team member
      const otherAcceptedParticipants = updatedParticipants.filter(p => p.status === 'accepted' && p.userId !== user.uid && p.userId !== match.organizerId)
      const teamNotifyPromises = otherAcceptedParticipants.map(participant =>
        sendMatchNotification({
          userId: participant.userId,
          type: 'new_team_member',
          messageI18nKey: 'matchDetails.newTeamMemberNotification',
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })
      )
      await Promise.all(teamNotifyPromises)
    } catch (error) {
      console.error('Error accepting invite:', error)
      toast.error(t('matchDetails.errorAcceptingInvite'))
    }
  }

  const handleDeclineInvite = async () => {
    if (!match || !user) return

    try {
      const updatedParticipants = match.participants.filter(p => p.userId !== user.uid)
      
      // Re-assign order numbers after removal
      const validatedParticipants = ensureValidOrderNumbers(updatedParticipants)

      await updateDocument('matches', id, {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      })

      setParticipantStatus(null)
      toast.success(t('matchDetails.inviteDeclined'))

      const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
      const titleParams = matchTitleI18nParams(match)

      await sendMatchNotification({
        userId: match.organizerId,
        type: 'invite_declined',
        messageI18nKey: 'matchDetails.inviteDeclinedNotification',
        messageI18nParams: { ...titleParams, senderName },
        extra: {
          senderId: user.uid,
          senderName
        }
      })
    } catch (error) {
      console.error('Error declining invite:', error)
      toast.error(t('matchDetails.errorDecliningInvite'))
    }
  }

  const handleAcceptPlayer = async (playerId) => {
    if (match.organizerId !== user?.uid) {
      toast.error(t('matchDetails.onlyOrganizerAccept'))
      return
    }

    const mainCapacity = getMatchRosterCapacity(match)
    const acceptedOnRoster = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (mainCapacity > 0 && acceptedOnRoster >= mainCapacity) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    try {
      const targetParticipant = match.participants?.find((participant) => participant.userId === playerId)
      const updatedParticipants = match.participants.map(p => {
        if (p.userId === playerId) {
          return { ...p, status: 'accepted' }
        }
        return p
      })

      let validatedParticipants = ensureValidOrderNumbers(updatedParticipants)
      validatedParticipants = pruneInvitedIfRosterFull(validatedParticipants, match)

      await updateDocument('matches', id, {
        participants: validatedParticipants,
        currentPlayers: (match.currentPlayers || 0) + 1,
        updatedAt: new Date().toISOString()
      })

      toast.success(t('matchDetails.playerAccepted'))

      if (targetParticipant?.userId && targetParticipant.userId !== user.uid) {
        const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)

        await sendMatchNotification({
          userId: targetParticipant.userId,
          type: 'player_accepted',
          messageI18nKey: 'matchDetails.playerAcceptedNotification',
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })

        // Also notify other accepted participants about the new team member
        const otherAcceptedParticipants = validatedParticipants.filter(p => p.status === 'accepted' && p.userId !== playerId && p.userId !== user.uid)
        const teamNotifyPromises = otherAcceptedParticipants.map(participant =>
          sendMatchNotification({
            userId: participant.userId,
            type: 'new_team_member',
            messageI18nKey: 'matchDetails.newTeamMemberNotification',
            messageI18nParams: { ...titleParams, senderName: targetParticipant.name || senderName },
            extra: {
              senderId: targetParticipant.userId,
              senderName: targetParticipant.name || senderName
            }
          })
        )
        await Promise.all(teamNotifyPromises)
      }
    } catch (error) {
      console.error('Error accepting player:', error)
      toast.error(t('matchDetails.errorAcceptingPlayer'))
    }
  }

  const handleRejectPlayer = async (playerId) => {
    if (match.organizerId !== user?.uid) {
      toast.error(t('matchDetails.onlyOrganizerReject'))
      return
    }

    try {
      const targetParticipant = match.participants?.find((participant) => participant.userId === playerId)
      const updatedParticipants = match.participants.filter(p => p.userId !== playerId)
      
      // Re-assign order numbers after removal
      const validatedParticipants = ensureValidOrderNumbers(updatedParticipants)

      await updateDocument('matches', id, {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      })

      toast.success(t('matchDetails.playerRejected'))

      if (targetParticipant?.userId && targetParticipant.userId !== user.uid) {
        const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)

        await sendMatchNotification({
          userId: targetParticipant.userId,
          type: 'player_rejected',
          messageI18nKey: 'matchDetails.playerDeclinedNotification',
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })
      }
    } catch (error) {
      console.error('Error rejecting player:', error)
      toast.error(t('matchDetails.errorRejectingPlayer'))
    }
  }

  const handleAssignTeam = async (playerId, team) => {
    if (!isOrganizer) {
      toast.error(t('matchDetails.onlyOrganizerAssignTeam', { defaultValue: 'Only organizer can assign teams' }))
      return
    }

    try {
      const updatedParticipants = (match.participants || []).map((participant) =>
        participant.userId === playerId ? { ...participant, team } : participant
      )

      const validatedParticipants = ensureValidOrderNumbers(updatedParticipants)

      await updateDocument('matches', id, {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      })

      toast.success(t('matchDetails.teamAssigned', { defaultValue: 'Team updated' }))
    } catch (error) {
      console.error('Error assigning team:', error)
      toast.error(t('matchDetails.teamAssignError', { defaultValue: 'Unable to assign team' }))
    }
  }

  const handleRemoveParticipant = async (playerId) => {
    if (!isOrganizer) {
      toast.error(t('matchDetails.onlyOrganizerRemove', { defaultValue: 'Only organizer can remove players' }))
      return
    }

    try {
      const participantToRemove = (match.participants || []).find((participant) => participant.userId === playerId)
      const updatedParticipants = (match.participants || []).filter((participant) => participant.userId !== playerId)
      
      // Re-assign order numbers after removal
      const validatedParticipants = ensureValidOrderNumbers(updatedParticipants)

      const updateData = {
        participants: validatedParticipants,
        updatedAt: new Date().toISOString()
      }

      if (participantToRemove?.status === 'accepted') {
        updateData.currentPlayers = Math.max((match.currentPlayers || 1) - 1, 0)
      }

      await updateDocument('matches', id, updateData)
      toast.success(t('matchDetails.playerRemoved', { defaultValue: 'Player removed' }))

      // Notify removed player (whether pending join request or invited)
      if (participantToRemove?.userId && participantToRemove.userId !== user.uid) {
        const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
        const titleParams = matchTitleI18nParams(match)
        const notificationType = participantToRemove?.status === 'pending' ? 'request_rejected' : 'invite_cancelled'
        const messageI18nKey = participantToRemove?.status === 'pending' 
          ? 'matchDetails.requestRejectedNotification' 
          : 'matchDetails.inviteCancelledNotification'

        await sendMatchNotification({
          userId: participantToRemove.userId,
          type: notificationType,
          messageI18nKey: messageI18nKey,
          messageI18nParams: { ...titleParams, senderName },
          extra: {
            senderId: user.uid,
            senderName
          }
        })
      }
    } catch (error) {
      console.error('Error removing player:', error)
      toast.error(t('matchDetails.playerRemoveError', { defaultValue: 'Unable to remove player' }))
    }
  }

  const handleCancelMatch = async () => {
    if (match.organizerId !== user?.uid) {
      toast.error(t('matchDetails.onlyOrganizerCancel'))
      return
    }

    const cancelReason = window.prompt(t('matchDetails.commentPrompt'))
    if (cancelReason === null) return
    if (!cancelReason.trim()) {
      toast.error(t('matchDetails.cancellationReasonRequired'))
      return
    }

    if (!window.confirm(t('matchDetails.cancelConfirm'))) {
      return
    }

    try {
      await updateDocument('matches', id, {
        status: 'cancelled',
        cancelReason: cancelReason.trim(),
        updatedAt: new Date().toISOString()
      })

      const participants = match.participants?.filter(p => p.userId !== user.uid) || []
      const titleParams = matchTitleI18nParams(match)
      const reasonTrimmed = cancelReason.trim()
      const cancelKey = 'matchDetails.cancelledMessage'
      const cancelParams = { ...titleParams, reason: reasonTrimmed }
      const notifPromises = participants.map((participant) =>
        sendMatchNotification({
          userId: participant.userId,
          type: 'match_update',
          messageI18nKey: cancelKey,
          messageI18nParams: cancelParams
        })
      )

      await Promise.all(notifPromises)

      toast.success(t('matchDetails.matchCancelled'))
      navigate('/matches')
    } catch (error) {
      console.error('Error cancelling match:', error)
      toast.error(t('matchDetails.errorCancellingMatch'))
    }
  }

  // Order: single list for the match. Organizer is always #1; others follow join order (joinedAt).
  const ensureValidOrderNumbers = (
    participants,
    organizerIdParam = match?.organizerId,
    organizerImageParam = match?.organizerImage
  ) => {
    if (!participants || participants.length === 0) return []

    const normalized = participants.map((participant) => {
      const p = { ...participant }
      if (p.isReserve) {
        p.isReserve = false
      }
      return p
    })

    const organizerImg =
      typeof organizerImageParam === 'string' && organizerImageParam.trim()
        ? organizerImageParam.trim()
        : ''
    if (organizerIdParam && organizerImg) {
      const orgParticipant = normalized.find((p) => p.userId === organizerIdParam)
      const hasProfile =
        orgParticipant &&
        typeof orgParticipant.profileImage === 'string' &&
        orgParticipant.profileImage.trim()
      if (orgParticipant && !hasProfile) {
        orgParticipant.profileImage = organizerImg
      }
    }

    normalized.forEach((participant) => {
      if (participant.status !== 'accepted') {
        delete participant.orderNumber
      }
    })

    const acceptedRoster = normalized.filter((p) => p.status === 'accepted')
    const sorted = [...acceptedRoster].sort((a, b) => {
      const aOrg = a.userId === organizerIdParam
      const bOrg = b.userId === organizerIdParam
      if (aOrg && !bOrg) return -1
      if (!aOrg && bOrg) return 1
      return String(a.joinedAt || '').localeCompare(String(b.joinedAt || ''))
    })

    sorted.forEach((participant, index) => {
      participant.orderNumber = index + 1
    })

    return normalized
  }

  const buildSharePayload = () => {
    const url = `${window.location.origin}/match/${id}`
    const matchTitle = match?.title || (match?.sport === 'calcio' ? t('matchDetails.footballMatch') : t('matchDetails.padelMatch'))
    const inviterName = match?.organizerName || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || t('matches.organizer', { defaultValue: 'Organizer' })
    const text = t('matchDetails.shareMessage', {
      defaultValue: 'Join me for {{matchTitle}} on Giochiamo: {{url}}',
      matchTitle,
      url
    })

    const whatsappText = t('matchDetails.shareWhatsappMessage', {
      defaultValue: '{{inviterName}} ti ha invitato a {{matchTitle}}\n{{url}}',
      inviterName,
      matchTitle,
      url
    })

    return { url, text, whatsappText, matchTitle, inviterName }
  }

  const buildCheckInUrl = () => `${window.location.origin}/match/${id}?checkin=true`

  const sendMatchNotification = async ({ userId, type, messageI18nKey, messageI18nParams, extra = {} }) => {
    if (!userId) return

    await createNotification({
      userId,
      type,
      messageI18nKey,
      messageI18nParams,
      message: resolveNotificationMessage(t, {
        messageI18nKey,
        messageI18nParams,
        message: ''
      }),
      matchId: id,
      ...extra
    })
  }

  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }

    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'absolute'
    textArea.style.left = '-9999px'
    document.body.appendChild(textArea)
    textArea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)
    return copied
  }

  const shareMatch = () => {
    if (!match || match.organizerId !== user?.uid) {
      toast.error(t('matchDetails.onlyOrganizerShare', { defaultValue: 'Only the organizer can share this match.' }))
      return
    }
    const cap = getMatchRosterCapacity(match)
    const accepted = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (cap > 0 && accepted >= cap) {
      return
    }
    setShowShareModal(true)
  }

  const handleShareAction = async (channel) => {
    if (!match || match.organizerId !== user?.uid) {
      setShowShareModal(false)
      return
    }

    const { url, text, whatsappText } = buildSharePayload()

    try {
      if (channel === 'copy') {
        const copied = await copyToClipboard(url)
        if (!copied) throw new Error('Unable to copy share URL')
        toast.success(t('matchDetails.copyLink'))
        return
      }

      if (channel === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, '_blank', 'noopener,noreferrer')
        setShowShareModal(false)
        return
      }

      if (channel === 'facebook') {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer')
        setShowShareModal(false)
        return
      }

      if (channel === 'instagram') {
        const copied = await copyToClipboard(url)
        if (copied) {
          toast.success(t('matchDetails.instagramCopyHint', { defaultValue: 'Link copied. Paste it in Instagram.' }))
        }
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer')
        setShowShareModal(false)
      }
    } catch (error) {
      console.error('Share action failed:', error)
      toast.error(t('matchDetails.shareFailed', { defaultValue: 'Unable to share right now.' }))
    }
  }

  const openInviteModal = async () => {
    if (!isOrganizer) return

    const mainCapacity = getMatchRosterCapacity(match)
    const acceptedOnRoster = match?.participants?.filter((p) => p.status === 'accepted').length || 0
    if (mainCapacity > 0 && acceptedOnRoster >= mainCapacity) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    setShowInviteModal(true)
    setInviteSearch('')
    setInvitePlayers([])
    setInvitedPlayerIds([]) // Clear previous invited IDs to prevent stale state
    setInviteLoading(true)

    try {
      // Fetch all users
      const usersSnapshot = await getDocuments('users')
      const participantIds = new Set((match?.participants || []).map((participant) => participant.userId))
      
      // Get all users
      const allUsers = usersSnapshot.docs.map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
      
      // Filter out self and organizer
      let candidates = allUsers.filter(c => c.id !== user?.uid && c.id !== match?.organizerId)
      
      // Filter to only include users who are NOT current participants
      // This allows re-inviting users who previously joined and left the match
      candidates = candidates.filter(candidate => !participantIds.has(candidate.id))

      // Exclude administrative roles (super admin, admin, center manager)
      const excludedRoles = new Set([
        'super_admin',
        'super_admin',
        'admin',
        'center_manager',
        'centermanager',
        'center-manager',
        'centerr_manager',
        'center_manager_'
      ])

      candidates = candidates.filter((candidate) => {
        const raw = String(candidate.role || '')
        const norm = raw.trim().toLowerCase().replace(/\s+/g, '_')
        return !excludedRoles.has(norm)
      })

      // Reset invited IDs - users can be re-invited regardless of past invites
      setInvitedPlayerIds([])

      setInvitePlayers(candidates)
    } catch (error) {
      console.error('Error loading invite players:', error)
      toast.error(t('matchDetails.inviteLoadError', { defaultValue: 'Unable to load players' }))
    } finally {
      setInviteLoading(false)
    }
  }

  const closeInviteModal = () => {
    setShowInviteModal(false)
    setInviteSearch('')
    setInvitePlayers([])
    setInviteLoading(false)
    setInviteSendingId(null)
    setInviteNearMeActive(true)
    setInvitedPlayerIds([])
  }

  const handleSendInvite = async (targetPlayer) => {
    if (!match || !targetPlayer || !user) return

    const mainCapacity = getMatchRosterCapacity(match)
    const acceptedOnRoster = match.participants?.filter((p) => p.status === 'accepted').length || 0
    if (mainCapacity > 0 && acceptedOnRoster >= mainCapacity) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    const senderName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.email || t('common.player')
    const inviteKey = 'matchDetails.inviteNotificationMessage'
    const inviteParams = { senderName, ...matchTitleI18nParams(match) }

    setInviteSendingId(targetPlayer.id)

    try {
      await sendMatchNotification({
        userId: targetPlayer.id,
        type: 'match_invite',
        messageI18nKey: inviteKey,
        messageI18nParams: inviteParams,
        extra: {
          senderId: user.uid,
          senderName
        }
      })

      const canAddInvitedRow = mainCapacity <= 0 || acceptedOnRoster < mainCapacity
      if (canAddInvitedRow) {
        const newParticipant = {
          userId: targetPlayer.id,
          name: `${targetPlayer.firstName || ''} ${targetPlayer.lastName || ''}`.trim(),
          role: targetPlayer.role || '',
          profileImage: targetPlayer.profileImage || '',
          status: 'invited',
          joinedAt: new Date().toISOString()
        }

        const updatedParticipants = [...(match.participants || []), newParticipant]

        let validatedParticipants = ensureValidOrderNumbers(updatedParticipants)
        validatedParticipants = pruneInvitedIfRosterFull(validatedParticipants, match)

        await updateDocument('matches', id, {
          participants: validatedParticipants,
          updatedAt: new Date().toISOString()
        })
      }

      setInvitedPlayerIds((prev) => (prev.includes(targetPlayer.id) ? prev : [...prev, targetPlayer.id]))
      toast.success(t('matchDetails.inviteSent', { defaultValue: 'Invitation sent' }))
    } catch (error) {
      console.error('Error sending invitation:', error)
      toast.error(t('matchDetails.inviteError', { defaultValue: 'Error sending invitation' }))
    } finally {
      setInviteSendingId(null)
    }
  }

  const inviteNearMeOrgOk = Number.isFinite(Number(currentUserData?.latitude)) && Number.isFinite(Number(currentUserData?.longitude))

  const filteredInvitePlayers = useMemo(() => {
    const q = inviteSearch.trim().toLowerCase()
    const searchFiltered = invitePlayers.filter((candidate) => {
      if (!q) return true
      return [candidate.firstName, candidate.lastName, `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim(), candidate.email, candidate.city]
        .some((value) => String(value || '').toLowerCase().includes(q))
    })

    const orgLat = Number(currentUserData?.latitude)
    const orgLon = Number(currentUserData?.longitude)
    const orgOk = Number.isFinite(orgLat) && Number.isFinite(orgLon)

    const passesNearMe = (candidate) => {
      if (!orgOk) return true
      const candidateLat = Number(candidate.latitude)
      const candidateLon = Number(candidate.longitude)
      const hasCandidateCoordinates = Number.isFinite(candidateLat) && Number.isFinite(candidateLon)
      const sameCity = Boolean(
        String(currentUserData?.city || '').trim().toLowerCase()
        && String(candidate.city || '').trim().toLowerCase()
        && String(currentUserData.city).trim().toLowerCase() === String(candidate.city).trim().toLowerCase()
      )
      if (hasCandidateCoordinates) {
        return calculateDistance(orgLat, orgLon, candidateLat, candidateLon) <= 150
      }
      return sameCity
    }

    const filtered =
      inviteNearMeActive && orgOk ? searchFiltered.filter(passesNearMe) : searchFiltered

    const distanceKm = (player) => {
      if (!orgOk) return Number.POSITIVE_INFINITY
      const lat = Number(player?.latitude)
      const lon = Number(player?.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return Number.POSITIVE_INFINITY
      return calculateDistance(orgLat, orgLon, lat, lon)
    }

    const isSameCity = (player) => {
      if (!currentUserData?.city || !player?.city) return false
      return String(currentUserData.city).trim().toLowerCase() === String(player.city).trim().toLowerCase()
    }

    const nameCompare = (a, b) => {
      const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase()
      const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase()
      return aName.localeCompare(bName)
    }

    if (inviteNearMeActive && orgOk) {
      return [...filtered].sort((a, b) => {
        const aSameCity = isSameCity(a)
        const bSameCity = isSameCity(b)
        // Same city players first
        if (aSameCity && !bSameCity) return -1
        if (!aSameCity && bSameCity) return 1
        // Then by distance
        const dA = distanceKm(a)
        const dB = distanceKm(b)
        if (dA !== dB) return dA - dB
        return nameCompare(a, b)
      })
    }

    // Default sort: same city first, then alphabetical
    return [...filtered].sort((a, b) => {
      const aSameCity = isSameCity(a)
      const bSameCity = isSameCity(b)
      if (aSameCity && !bSameCity) return -1
      if (!aSameCity && bSameCity) return 1
      return nameCompare(a, b)
    })
  }, [
    invitePlayers,
    inviteSearch,
    inviteNearMeActive,
    currentUserData?.latitude,
    currentUserData?.longitude,
    currentUserData?.city
  ])

  const getInvitePlayerRoleLabel = (player) => {
    const roleKey = player?.role
    if (!roleKey || typeof roleKey !== 'string') {
      return t('matchDetails.inviteRoleNotSet', { defaultValue: 'Role not set' })
    }
    return t(`role.${roleKey}`, { defaultValue: roleKey })
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  if (!match) return null

  const isOrganizer = match.organizerId === user?.uid
  const isAccepted = participantStatus === 'accepted'
  const acceptedCount = match.participants?.filter(p => p.status === 'accepted').length || 0
  const mainRosterCapacity = getMatchRosterCapacity(match)
  const participantCheckIns =
    match.participants?.filter((p) => p.status === 'accepted' && p.checkedInAt).length || 0
  const staffVenueCheckInCount = match.staffVenueCheckIns
    ? Object.values(match.staffVenueCheckIns).filter((entry) => entry && entry.checkedInAt).length
    : 0
  const checkedInCount = participantCheckIns + staffVenueCheckInCount
  const pendingCount = match.participants?.filter(p => p.status === 'pending').length || 0
  const invitedCount = match.participants?.filter(p => p.status === 'invited').length || 0
  const searchedParticipants = match.participants?.filter(p => 
    !playerSearch || p.name?.toLowerCase().includes(playerSearch.toLowerCase())
  ) || []
  const acceptedParticipants = searchedParticipants.filter((participant) => participant.status === 'accepted')
  const pendingParticipants = searchedParticipants.filter((participant) => participant.status === 'pending')
  const invitedParticipants = searchedParticipants.filter((participant) => participant.status === 'invited')
  const totalRosterCapacity = mainRosterCapacity
  const isRosterFull = mainRosterCapacity > 0 && acceptedCount >= mainRosterCapacity
  const byOrderThenName = (a, b) => {
    const aOrder = Number(a.orderNumber)
    const bOrder = Number(b.orderNumber)
    const aHasOrder = Number.isInteger(aOrder)
    const bHasOrder = Number.isInteger(bOrder)
    if (aHasOrder && bHasOrder && aOrder !== bOrder) return aOrder - bOrder
    if (aHasOrder && !bHasOrder) return -1
    if (!aHasOrder && bHasOrder) return 1
    return String(a.joinedAt || '').localeCompare(String(b.joinedAt || ''))
  }
  const acceptedRosterSorted = [...acceptedParticipants].sort(byOrderThenName)
  const derivedStatus = getDerivedMatchStatus(match.date, match.status)
  const isPast = derivedStatus === 'completed'
  const isCancelled = derivedStatus === 'cancelled'
  // Organizer can chat only when match is upcoming/ongoing
  const canChat = isCancelled ? false : (isOrganizer ? (derivedStatus === 'upcoming' || derivedStatus === 'ongoing') : isAccepted)
  const showSidebarActionsCard =
    isCancelled || !isPast || (isPast && !isCancelled && isOrganizer)

  const renderParticipantCard = (participant) => {
    const cardBgClass =
      participant.team === 'red'
        ? 'bg-red-950/35 border border-red-500/25 dark:bg-red-950/45 dark:border-red-500/30'
        : participant.team === 'blue'
          ? 'bg-blue-950/35 border border-blue-500/25 dark:bg-blue-950/45 dark:border-blue-500/30'
          : 'bg-gray-50 dark:bg-gray-700 border border-transparent'

    const avatarRingClass =
      participant.team === 'red'
        ? 'ring-2 ring-red-400/50'
        : participant.team === 'blue'
          ? 'ring-2 ring-blue-400/50'
          : 'ring-2 ring-sport-primary/30'

    const avatarTitle =
      participant.team === 'red'
        ? t('matchDetails.teamRed', { defaultValue: 'Red Team' })
        : participant.team === 'blue'
          ? t('matchDetails.teamBlue', { defaultValue: 'Blue Team' })
          : t('matchDetails.teamNotAssigned', { defaultValue: 'Team not assigned' })

    const trimUrl = (value) => {
      if (typeof value !== 'string') return null
      const t = value.trim()
      return t || null
    }
    const fromParticipant = trimUrl(participant.profileImage)
    const isOrganizerRow = participant.userId === match.organizerId
    const fromMatchOrganizer = isOrganizerRow ? trimUrl(match.organizerImage) : null
    const fromCurrentUser = participant.userId === user?.uid ? trimUrl(userData?.profileImage) : null
    const profileSrc = fromParticipant || fromMatchOrganizer || fromCurrentUser

    return (
      <div key={participant.userId} className={`rounded-lg p-3 ${cardBgClass}`}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <ParticipantAvatar
              profileSrc={profileSrc}
              name={participant.name}
              className={`relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary text-sm font-bold text-white ${avatarRingClass}`}
              title={avatarTitle}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {participant.orderNumber && participant.status === 'accepted' && (
                  <span
                    className="shrink-0 rounded-md bg-sport-primary/15 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-sport-primary"
                    title={t('matchDetails.orderNumber', { defaultValue: 'Order #' })}
                  >
                    #{String(participant.orderNumber).padStart(2, '0')}
                  </span>
                )}
                <p className="truncate font-medium leading-tight text-gray-900 dark:text-white">
                  {participant.name || t('common.player', { defaultValue: 'Player' })}
                </p>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                {participant.role === 'goalkeeper' && <span>{`🧤 ${t('role.goalkeeper')}`}</span>}
                {participant.role === 'defender' && <span>{`🛡️ ${t('role.defender')}`}</span>}
                {participant.role === 'midfielder' && <span>{`⚡ ${t('role.midfielder')}`}</span>}
                {participant.role === 'attacker' && <span>{`🎯 ${t('role.attacker')}`}</span>}
                {participant.role === 'left' && <span>{`⬅️ ${t('role.left')}`}</span>}
                {participant.role === 'right' && <span>{`➡️ ${t('role.right')}`}</span>}
                {participant.checkedInAt && (
                  <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">
                    {t('matchDetails.checkedIn')}
                  </span>
                )}
                {participant.status === 'accepted' && (
                  <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    {t('matchDetails.mainPlayer', { defaultValue: 'Player' })}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 xl:ml-4 xl:flex-nowrap xl:justify-end">
            {participant.status === 'pending' && isOrganizer && (
              <>
                <button
                  onClick={() => handleAcceptPlayer(participant.userId)}
                  disabled={isRosterFull}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-green-600 hover:bg-green-100 hover:text-green-800 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-green-900/20"
                  title={t('matchDetails.acceptPlayer', { defaultValue: 'Accept player' })}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleRejectPlayer(participant.userId)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-red-600 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900/20"
                  title={t('matchDetails.rejectPlayer', { defaultValue: 'Reject player' })}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </>
            )}
            {participant.status === 'pending' && (
              <span className="text-xs text-yellow-600 flex items-center whitespace-nowrap">
                <WaitingIcon className="h-4 w-4 mr-1" />
                {t('common.pending')}
              </span>
            )}
            {participant.status === 'invited' && (
              <span className="text-xs text-blue-600 flex items-center whitespace-nowrap">
                {t('matchDetails.invited', { defaultValue: 'Invited' })}
              </span>
            )}
            {participant.userId === match.organizerId && (
              <span className="text-xs bg-sport-primary/10 text-sport-primary px-2 py-1 rounded whitespace-nowrap">
                {t('matches.organizer')}
              </span>
            )}
            {isOrganizer && participant.status === 'accepted' && (
              <>
                <button
                  type="button"
                  onClick={() => handleAssignTeam(participant.userId, 'red')}
                  className={`h-8 rounded border px-1.5 sm:px-2.5 text-xs ${participant.team === 'red' ? 'border-red-500 text-red-200 bg-red-900/30' : 'border-gray-300 text-gray-600 dark:text-gray-300'}`}
                >
                  <span className="hidden sm:inline">{t('matchDetails.teamRed', { defaultValue: 'Red' })}</span>
                  <span className="sm:hidden">R</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAssignTeam(participant.userId, 'blue')}
                  className={`h-8 rounded border px-1.5 sm:px-2.5 text-xs ${participant.team === 'blue' ? 'border-blue-500 text-blue-200 bg-blue-900/30' : 'border-gray-300 text-gray-600 dark:text-gray-300'}`}
                >
                  <span className="hidden sm:inline">{t('matchDetails.teamBlue', { defaultValue: 'Blue' })}</span>
                  <span className="sm:hidden">B</span>
                </button>
              </>
            )}
            {isOrganizer && participant.userId !== match.organizerId && (
              <button
                type="button"
                onClick={() => handleRemoveParticipant(participant.userId)}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-red-600 hover:bg-red-100 hover:text-red-800 dark:hover:bg-red-900/20"
                title={t('matchDetails.removePlayer', { defaultValue: 'Remove player' })}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      {/* Header Image */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative h-64 rounded-2xl overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-sport-primary to-sport-secondary">
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">{match.sport === 'calcio' ? '⚽' : '🎾'}</span>
              <span className="text-sm bg-white/20 px-2 py-1 rounded">
                {match.matchType}
              </span>
            </div>
            <h1 className="text-3xl font-bold">{match.title || (match.sport === 'calcio' ? t('matchDetails.footballMatch') : t('matchDetails.padelMatch'))}</h1>
            <p className="mt-2 opacity-90">{t('matchDetails.organizedBy')} {match.organizerName}</p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Match Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('matchDetails.detailsTitle')}</h2>
            <div className="space-y-3">
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <CalendarIcon className="h-5 w-5 mr-3" />
                <span>{format(new Date(match.date), "EEEE, MMMM d, yyyy", { locale })}</span>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <ClockIcon className="h-5 w-5 mr-3" />
                <span>{format(new Date(match.date), 'HH:mm', { locale })} • {match.duration} {t('matchDetails.minutes')}</span>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-400">
                <MapPinIcon className="h-5 w-5 mr-3" />
                <span>{match.location}</span>
              </div>
              {match.address && (
                <div className="ml-8 text-sm text-gray-500">{match.address}</div>
              )}
              {match.price > 0 && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <CurrencyEuroIcon className="h-5 w-5 mr-3" />
                  <span>€{match.price} {t('matches.players')}</span>
                </div>
              )}
              {match.equipment && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <TrophyIcon className="h-5 w-5 mr-3" />
                  <span>{t('matchDetails.equipment')}: {match.equipment}</span>
                </div>
              )}
            </div>
            {match.description && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300">{match.description}</p>
              </div>
            )}

            {/* Mobile Action Buttons for Invited/Pending Users */}
            {participantStatus === 'invited' && !isPast && !isCancelled && (
              <div className="mt-4 lg:hidden space-y-3">
                <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                  {t('matchDetails.invitedToJoin', { defaultValue: 'You have been invited to join this match' })}
                </p>
                <button
                  type="button"
                  onClick={handleAcceptInvite}
                  disabled={isRosterFull}
                  title={isRosterFull ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
                  className="w-full btn-gradient flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {isRosterFull
                    ? t('matchDetails.matchFull', { defaultValue: 'Match full' })
                    : t('matchDetails.acceptInvite', { defaultValue: 'Accept' })}
                </button>
                <button
                  type="button"
                  onClick={handleDeclineInvite}
                  className="w-full bg-red-500 text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition flex items-center justify-center gap-2"
                >
                  <XCircleIcon className="h-5 w-5" />
                  {t('matchDetails.declineInvite', { defaultValue: 'Decline' })}
                </button>
              </div>
            )}

            {participantStatus === 'pending' && !isPast && !isCancelled && (
              <div className="mt-4 lg:hidden">
                <button
                  onClick={handleLeaveMatch}
                  className="w-full bg-yellow-500 text-white py-3 rounded-xl font-semibold hover:bg-yellow-600 transition"
                >
                  {t('matchDetails.requestPendingCancel')}
                </button>
              </div>
            )}
          </motion.div>

          {/* Participants List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
          >
            <div className="mb-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h2 className="min-w-0 text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {t('matchDetails.participants')} ({acceptedCount}/{mainRosterCapacity})
                  <span className="ml-2 inline-flex items-center text-sm font-normal text-cyan-700 bg-cyan-100 dark:bg-cyan-900/30 dark:text-cyan-300 px-2 py-1 rounded-full whitespace-nowrap">
                    {t('matchDetails.checkedInCount', { count: checkedInCount, total: acceptedCount })}
                  </span>
                  {isOrganizer && pendingCount > 0 && (
                    <span className="ml-2 inline-flex items-center text-sm font-normal text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-full whitespace-nowrap">
                      {pendingCount} {t('matchDetails.pendingRequests')}
                    </span>
                  )}
                </h2>
                {(isOrganizer || isAccepted) && (
                  <button
                    onClick={() => setShowQR(!showQR)}
                    className="shrink-0 text-xs sm:text-sm text-right text-sport-primary hover:text-sport-secondary whitespace-nowrap"
                  >
                    {t('matchDetails.qrCheckIn')}
                  </button>
                )}
              </div>

              <input
                type="text"
                placeholder={t('matchDetails.searchPlayers')}
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-sport-primary focus:border-transparent"
              />
            </div>

            {showQR && (isOrganizer || isAccepted) && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                <QRCodeSVG value={buildCheckInUrl()} size={150} className="mx-auto" />
                <p className="mt-2 text-sm text-gray-600">{t('matchDetails.scanForCheckIn')}</p>
              </div>
            )}

            <div className="space-y-4">
              {acceptedRosterSorted.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('matchDetails.matchPlayers', { defaultValue: 'Players' })}
                  </h3>
                  <div className="space-y-2">
                    {acceptedRosterSorted.map((participant) => renderParticipantCard(participant))}
                  </div>
                </div>
              )}

              {pendingParticipants.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('matchDetails.pendingPlayers', { defaultValue: 'Pending Requests' })}
                  </h3>
                  <div className="space-y-2">
                    {pendingParticipants.map((participant) => renderParticipantCard(participant))}
                  </div>
                </div>
              )}

              {invitedParticipants.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('matchDetails.invitedPlayers', { defaultValue: 'Invited Players' })}
                  </h3>
                  <div className="space-y-2">
                    {invitedParticipants.map((participant) => renderParticipantCard(participant))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Chat Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
          >
            <button
              onClick={handleToggleChat}
              className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            >
              <div className="flex items-center space-x-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-sport-primary" />
                <span className="font-semibold">{t('matchDetails.groupChat')}</span>
                {unreadCount > 0 && !showChat && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <span>{showChat ? '▼' : '▲'}</span>
            </button>
            
            {showChat && (
              <div className="p-4">
                {isCancelled ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                      <XCircleIcon className="h-6 w-6 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('matchDetails.matchCancelledTitle')}</h3>
                    {match.cancelReason ? (
                      <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mt-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('matchDetails.reasonFromOrganizer')}</p>
                        <p className="text-gray-900 dark:text-white">{match.cancelReason}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500">{t('matchDetails.cancelledNotice')}</p>
                    )}
                  </div>
                ) : !canChat ? (
                  <div className="text-center text-gray-500 py-8">
                    {!participantStatus ? (
                      <p>{t('matchDetails.joinChatPrompt')}</p>
                    ) : participantStatus === 'pending' ? (
                      <p>{t('matchDetails.waitAcceptancePrompt')}</p>
                    ) : (
                      <p>{t('matchDetails.acceptedOnlyPrompt')}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      ref={chatMessagesScrollRef}
                      className="h-96 overflow-y-auto mb-4 space-y-3 pr-2 overscroll-y-contain touch-pan-y"
                    >
                      {chatMessages.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                          {t('matchDetails.noMessagesYet')}
                        </div>
                      ) : (
                        chatMessages.map((msg) => {
                          const isOwn = msg.senderId === user?.uid
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex items-start space-x-2 max-w-[80%] ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                <div className="h-8 w-8 rounded-full bg-sport-primary/20 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {msg.sender?.[0]}
                                </div>
                                <div className={`${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                                  <p className={`text-xs font-medium mb-1 ${isOwn ? 'text-right' : ''}`}>{msg.sender}</p>
                                  {msg.replyTo && (
                                    <div className="mb-1 px-2 py-1 rounded border-l-2 border-sport-primary bg-gray-100 dark:bg-gray-600 text-xs max-w-full">
                                      <p className="font-medium text-sport-primary">{msg.replyTo.sender}</p>
                                      <p className="text-gray-500 dark:text-gray-400 truncate">{msg.replyTo.message}</p>
                                    </div>
                                  )}
                                  <div className={`px-3 py-2 rounded-xl ${isOwn ? 'bg-sport-primary text-white rounded-br-none' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'}`}>
                                    <p className="text-sm whitespace-pre-wrap break-words">
                                      {msg.message.split(/(@\w+)/g).map((part, i) =>
                                        part.startsWith('@') ? (
                                          <span key={i} className="font-bold text-yellow-300 dark:text-yellow-400">{part}</span>
                                        ) : (
                                          <span key={i}>{part}</span>
                                        )
                                      )}
                                    </p>
                                  </div>
                                  <div className={`flex items-center gap-2 mt-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                                    <p className="text-xs text-gray-400">
                                      {msg.createdAt?.toDate ? format(msg.createdAt.toDate(), 'HH:mm') : ''}
                                    </p>
                                    {canChat && (
                                      <button
                                        onClick={() => setReplyTo(msg)}
                                        className="text-xs text-gray-400 hover:text-sport-primary transition flex items-center gap-0.5"
                                      >
                                        <ArrowUturnLeftIcon className="h-3 w-3" />
                                        {t('matchDetails.reply')}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>

                    {showMentions && filteredParticipants.length > 0 && (
                      <div className="mb-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 max-h-32 overflow-y-auto">
                        {filteredParticipants.map((p) => (
                          <button
                            key={p.userId}
                            onClick={() => insertMention(p.name)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center space-x-2 text-sm"
                          >
                            <div className="h-6 w-6 rounded-full bg-sport-primary/20 flex items-center justify-center text-xs font-bold">
                              {p.name?.[0]}
                            </div>
                            <span>{p.name}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {replyTo && (
                      <div className="mb-2 flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 border-l-2 border-sport-primary">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-sport-primary">{t('matchDetails.replyingTo')} {replyTo.sender}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{replyTo.message}</p>
                        </div>
                        <button
                          onClick={() => setReplyTo(null)}
                          className="text-gray-400 hover:text-gray-600 ml-2"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={message}
                        onChange={handleInputChange}
                        placeholder={t('matchDetails.writeMessage')}
                        className="flex-1 input-modern"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSendMessage()
                          }
                        }}
                      />
                      <button
                        onClick={handleSendMessage}
                        className="btn-gradient py-2 px-4"
                      >
                        {t('matchDetails.send')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Action Buttons */}
          {showSidebarActionsCard && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              {isCancelled ? (
                <div className="text-center py-2 mb-3">
                  <span className="text-red-500 font-semibold">{t('matchDetails.thisMatchCancelled')}</span>
                </div>
              ) : !isPast ? (
                <>
                  {!participantStatus ? (
                    <button
                      type="button"
                      onClick={handleJoinMatch}
                      disabled={isRosterFull}
                      title={isRosterFull ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
                      className="w-full btn-gradient mb-3 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UserPlusIcon className="h-5 w-5 inline mr-2" />
                      {isRosterFull ? t('matchDetails.matchFull') : t('matchDetails.joinMatch')}
                    </button>
                  ) : participantStatus === 'invited' ? (
                    <div className="hidden lg:block space-y-3">
                      <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-2">
                        {t('matchDetails.invitedToJoin', { defaultValue: 'You have been invited to join this match' })}
                      </p>
                      <button
                        type="button"
                        onClick={handleAcceptInvite}
                        disabled={isRosterFull}
                        title={isRosterFull ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
                        className="w-full btn-gradient flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                        {isRosterFull
                          ? t('matchDetails.matchFull', { defaultValue: 'Match full' })
                          : t('matchDetails.acceptInvite', { defaultValue: 'Accept' })}
                      </button>
                      <button
                        type="button"
                        onClick={handleDeclineInvite}
                        className="w-full bg-red-500 text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition flex items-center justify-center gap-2"
                      >
                        <XCircleIcon className="h-5 w-5" />
                        {t('matchDetails.declineInvite', { defaultValue: 'Decline' })}
                      </button>
                    </div>
                  ) : participantStatus === 'pending' ? (
                    <button
                      onClick={handleLeaveMatch}
                      className="hidden w-full bg-yellow-500 text-white py-3 rounded-xl font-semibold mb-3 hover:bg-yellow-600 transition lg:block"
                    >
                      {t('matchDetails.requestPendingCancel')}
                    </button>
                  ) : participantStatus === 'accepted' ? (
                    <button
                      onClick={handleLeaveMatch}
                      className="w-full bg-red-500 text-white py-3 rounded-xl font-semibold mb-3 hover:bg-red-600 transition"
                    >
                      {isOrganizer ? t('matchDetails.leaveMatchAsPlayer') : t('matchDetails.leaveMatch')}
                    </button>
                  ) : null}

                  {isOrganizer && !isPast && !isCancelled && (
                    <button
                      onClick={openInviteModal}
                      disabled={isRosterFull}
                      className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold mb-3 hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UserPlusIcon className="h-5 w-5" />
                      {t('matchDetails.searchAvailablePlayers')}
                    </button>
                  )}

                  {isOrganizer && !isPast && !isCancelled && (
                    <button
                      onClick={handleCancelMatch}
                      className="w-full bg-red-600 text-white py-3 rounded-xl font-semibold mb-3 hover:bg-red-700 transition flex items-center justify-center gap-2"
                    >
                      <TrashIcon className="h-5 w-5" />
                      {t('matchDetails.cancelMatch')}
                    </button>
                  )}
                </>
              ) : null}

              {isOrganizer && (
                <button
                  type="button"
                  onClick={shareMatch}
                  disabled={isRosterFull}
                  title={isRosterFull ? t('matchDetails.matchFull', { defaultValue: 'Match is full' }) : undefined}
                  className="w-full btn-outline flex items-center justify-center space-x-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShareIcon className="h-5 w-5" />
                  <span>{t('matchDetails.shareMatch')}</span>
                </button>
              )}
            </motion.div>
          )}

          {/* Match Status */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
          >
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{t('matchDetails.statusTitle')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('matchDetails.status')}</span>
                <span className={`font-semibold ${isCancelled ? 'text-red-500' : derivedStatus === 'completed' ? 'text-gray-500' : derivedStatus === 'ongoing' ? 'text-blue-500' : 'text-green-500'}`}>
                  {isCancelled
                    ? t('matchDetails.cancelled')
                    : derivedStatus === 'completed'
                      ? t('matchDetails.completed')
                      : derivedStatus === 'ongoing'
                        ? t('matches.inProgress', { defaultValue: 'Ongoing' })
                        : t('matchDetails.upcoming')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('matchDetails.spotsAvailable')}</span>
                <span className="font-semibold">{Math.max(0, totalRosterCapacity - acceptedCount)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-sport-primary h-2 rounded-full transition-all"
                  style={{ width: `${totalRosterCapacity ? Math.min(100, (acceptedCount / totalRosterCapacity) * 100) : 0}%` }}
                ></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showInviteModal && isOrganizer && !isPast && !isCancelled && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeInviteModal} />
          <div className="relative w-full max-w-xl rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('matchDetails.searchAvailablePlayers')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('matchDetails.inviteSubtitle', { defaultValue: 'Choose a player to invite to this match.' })}
                </p>
              </div>
              <button
                type="button"
                onClick={closeInviteModal}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  placeholder={t('matchDetails.inviteSearchPlaceholder', { defaultValue: 'Search players to invite...' })}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-24 text-gray-900 dark:text-white focus:ring-2 focus:ring-sport-primary focus:border-transparent"
                />
                <button
                  type="button"
                  disabled={!inviteNearMeOrgOk}
                  onClick={() => {
                    if (inviteNearMeOrgOk) setInviteNearMeActive((prev) => !prev)
                  }}
                  title={
                    !inviteNearMeOrgOk
                      ? t('matchDetails.nearMeUnavailable', { defaultValue: 'Set your location in profile to filter by distance' })
                      : inviteNearMeActive
                        ? t('matchDetails.nearMeOnHint', { defaultValue: 'Near me: on — tap to show all players' })
                        : t('matchDetails.nearMeOffHint', { defaultValue: 'Near me: off — tap to filter by distance' })
                  }
                  className={`absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    !inviteNearMeOrgOk
                      ? 'cursor-not-allowed bg-gray-200 text-gray-500 opacity-60 dark:bg-gray-700 dark:text-gray-400'
                      : inviteNearMeActive
                        ? 'cursor-pointer bg-sport-secondary text-white hover:bg-sport-secondary/90'
                        : 'cursor-pointer bg-sport-primary/10 text-sport-primary hover:bg-sport-primary/20 dark:text-sport-primary'
                  }`}
                  aria-pressed={inviteNearMeOrgOk && inviteNearMeActive}
                >
                  <MapPinIcon className="h-4 w-4 shrink-0" />
                  <span>{t('matches.nearMe')}</span>
                </button>
              </div>

              <div className="max-h-[22rem] overflow-y-auto space-y-2 pr-1">
                {inviteLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="h-8 w-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredInvitePlayers.length > 0 ? (
                  filteredInvitePlayers.map((player) => {
                    const isInvited = invitedPlayerIds.includes(player.id)
                    const isSending = inviteSendingId === player.id
                    return (
                      <div
                        key={player.id}
                        className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/60 px-4 py-3 md:items-center"
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-3 md:min-w-0 md:items-center">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary text-sm font-bold text-white">
                            {player.firstName?.[0] || player.email?.[0] || '?'}
                          </div>
                          <div className="min-w-0 flex-1 pr-1">
                            <p className="break-words font-medium text-gray-900 dark:text-white max-md:text-[15px] md:truncate">
                              {[player.firstName, player.lastName].filter(Boolean).join(' ') || player.email || t('common.player')}
                            </p>
                            <p className="mt-0.5 break-words text-xs leading-snug text-gray-500 dark:text-gray-400 max-md:text-[13px] md:truncate">
                              <span className="max-md:inline md:hidden">{getInvitePlayerRoleLabel(player)}</span>
                              <span className="hidden md:inline">
                                {player.city || player.email || getInvitePlayerRoleLabel(player)}
                              </span>
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSendInvite(player)}
                          disabled={isSending || isInvited}
                          aria-label={
                            isInvited
                              ? t('matchDetails.invited', { defaultValue: 'Invited' })
                              : isSending
                                ? t('common.sending')
                                : t('matches.invite')
                          }
                          className={`inline-flex shrink-0 items-center justify-center rounded-xl text-xs font-semibold uppercase tracking-wider transition disabled:opacity-60 ${
                            isInvited
                              ? 'h-10 w-10 cursor-default border border-emerald-500/35 bg-emerald-500/10 p-0 text-emerald-500 dark:text-emerald-400 md:h-auto md:w-auto md:px-4 md:py-2'
                              : 'btn-gradient h-10 w-10 p-0 md:h-auto md:w-auto md:px-4 md:py-2'
                          }`}
                        >
                          {isSending ? (
                            <>
                              <span className="hidden md:inline">{t('common.loading', { defaultValue: 'Loading...' })}</span>
                              <span className="flex h-5 w-5 items-center justify-center md:hidden">
                                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              </span>
                            </>
                          ) : isInvited ? (
                            <>
                              <span className="hidden md:inline">{t('matchDetails.invited', { defaultValue: 'Invited' })}</span>
                              <CheckCircleSolidIcon className="h-6 w-6 md:hidden" aria-hidden />
                            </>
                          ) : (
                            <>
                              <span className="hidden md:inline">{t('matches.invite')}</span>
                              <PaperAirplaneIcon className="h-5 w-5 md:hidden" aria-hidden />
                            </>
                          )}
                        </button>
                      </div>
                    )
                  })
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('matchDetails.inviteNoPlayers', { defaultValue: 'No players found to invite.' })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && isOrganizer && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowShareModal(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('matchDetails.shareMatch')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('matchDetails.shareVia', { defaultValue: 'Choose where to share this match' })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleShareAction('copy')}
                className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/40 px-4 py-3 flex items-center gap-3 text-gray-800 dark:text-gray-100 hover:border-sport-primary hover:text-sport-primary transition"
              >
                <FaLink className="h-4 w-4" />
                <span className="text-sm font-semibold">{t('matchDetails.copyLink', { defaultValue: 'Copy link' })}</span>
              </button>

              <button
                type="button"
                onClick={() => handleShareAction('whatsapp')}
                className="rounded-xl border border-green-200 dark:border-green-500/40 bg-green-50 dark:bg-green-500/10 px-4 py-3 flex items-center gap-3 text-green-700 dark:text-green-300 hover:border-green-500 transition"
              >
                <FaWhatsapp className="h-4 w-4" />
                <span className="text-sm font-semibold">WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={() => handleShareAction('facebook')}
                className="rounded-xl border border-blue-200 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10 px-4 py-3 flex items-center gap-3 text-blue-700 dark:text-blue-300 hover:border-blue-500 transition"
              >
                <FaFacebookF className="h-4 w-4" />
                <span className="text-sm font-semibold">Facebook</span>
              </button>

              <button
                type="button"
                onClick={() => handleShareAction('instagram')}
                className="rounded-xl border border-pink-200 dark:border-pink-500/40 bg-pink-50 dark:bg-pink-500/10 px-4 py-3 flex items-center gap-3 text-pink-700 dark:text-pink-300 hover:border-pink-500 transition"
              >
                <FaInstagram className="h-4 w-4" />
                <span className="text-sm font-semibold">Instagram</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MatchDetails
